<template>
  <div class="planner-sheet-aba">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Lesson Plan Grid</h4>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <b-spinner variant="primary" class="mb-2" />
      <p class="mb-0 text-muted">Loading lesson plan grid...</p>
    </div>

    <div v-else-if="errorMessage" class="error-state">
      <p class="mb-0 text-danger">{{ errorMessage }}</p>
    </div>

    <div v-else class="print-page">
      <div class="sheet-border">
        <div class="sheet-header">
          <h1 class="sheet-title">Lesson Plan Grid</h1>
        </div>

        <div class="meta-grid">
          <div class="meta-row">
            <div class="meta-cell meta-label">Teacher's Name</div>
            <div class="meta-cell meta-value">{{ teacherName }}</div>
            <div class="meta-cell meta-label">Subject</div>
            <div class="meta-cell meta-value">{{ subjectName }}</div>
          </div>
          <div class="meta-row">
            <div class="meta-cell meta-label">Grade</div>
            <div class="meta-cell meta-value">{{ gradeName }}</div>
            <div class="meta-cell meta-label">Section</div>
            <div class="meta-cell meta-value">{{ sectionName }}</div>
          </div>
          <div class="meta-row">
            <div class="meta-cell meta-label">Academic Year</div>
            <div class="meta-cell meta-value meta-value--wide">
              {{ sessionName }}
            </div>
          </div>
        </div>

        <div class="grid-wrap">
          <table class="lesson-grid">
            <thead>
              <tr>
                <th class="col-index">#</th>
                <th class="col-concept">Concept</th>
                <th class="col-topic">Topic</th>
                <th class="col-subtopic">Subtopic</th>
                <th class="col-type">Type</th>
                <th
                  v-for="header in plannerHeaders"
                  :key="header.id"
                  class="col-period"
                >
                  {{ header.header }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!gridRows.length">
                <td :colspan="5 + plannerHeaders.length" class="empty-cell">
                  No planner details found.
                </td>
              </tr>
              <tr v-for="(row, index) in gridRows" :key="row.key">
                <td class="text-center">{{ index + 1 }}</td>
                <td>{{ row.concept || "—" }}</td>
                <td>{{ row.topic || "—" }}</td>
                <td>{{ row.subtopic || "—" }}</td>
                <td class="text-center">{{ row.type || "None" }}</td>
                <td
                  v-for="header in plannerHeaders"
                  :key="`${row.key}-${header.id}`"
                  class="period-cell"
                >
                  <span v-if="isClassTestOrQuiz(row.type)" class="type-flag">
                    (Class Tests/Quiz)
                  </span>
                  {{ row.headers[header.id] || "" }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BSpinner } from "bootstrap-vue";
import { mapActions } from "vuex";

export default {
  name: "PlannerSheetAba",
  components: {
    BSpinner,
  },
  data() {
    return {
      loading: true,
      errorMessage: "",
      sheetId: 0,
      campusId: 0,
      db: "",
      plannerSheet: {},
      plannerHeaders: [],
      concepts: [],
      topics: [],
      subtopics: [],
      classes: [],
      subjects: [],
      sessions: [],
      sections: [],
      teacherName: "—",
      gradeName: "—",
      subjectName: "—",
      sectionName: "—",
      sessionName: "—",
      gridRows: [],
      plannerDetails: [],
      originalPageTitle: "",
      autoPrint: false,
    };
  },
  async created() {
    this.originalPageTitle = document.title;
    document.title = " ";
    await this.initReport();
  },
  beforeDestroy() {
    if (this.originalPageTitle) {
      document.title = this.originalPageTitle;
    }
  },
  mounted() {
    if (this.autoPrint) {
      this.$nextTick(() => {
        setTimeout(() => this.print(), 500);
      });
    }
  },
  methods: {
    ...mapActions({ get: "get" }),

    async initReport() {
      const query = this.$route.query || {};
      this.sheetId = Number(query.id) || 0;
      this.db = query.db || this.$store.state.userData.db;
      this.campusId = Number(query.cID || this.$store.state.userData.cId);
      this.autoPrint = query.autoPrint === "1";
      this.gradeName = query.className || "—";
      this.subjectName = query.subjectName || "—";
      this.sessionName = query.sessionName || "—";
      this.sectionName = query.sectionName || "All";

      if (!this.sheetId) {
        this.errorMessage = "Planner sheet ID is missing.";
        this.loading = false;
        return;
      }

      try {
        await Promise.all([
          this.loadPlannerHeaders(),
          this.loadReferenceData(),
          this.loadSheetData(),
        ]);
        this.buildGridRows();
        this.resolveMetaLabels();
      } catch (error) {
        console.error("Planner sheet ABA report error:", error);
        this.errorMessage = "Failed to load planner sheet report.";
      } finally {
        this.loading = false;
      }
    },

    async loadPlannerHeaders() {
      const res = await this.get({
        url: `${this.$store.state.domain}PlannerHeaders?db=${this.db}&cId=${this.campusId}`,
        token: this.$store.state.userData.token,
      });
      this.plannerHeaders = Array.isArray(res) ? res : [];
    },

    async loadReferenceData() {
      const token = this.$store.state.userData.token;
      const domain = this.$store.state.domain;

      const [conceptsRes, topicsRes, subtopicsRes, classesRes, subjectsRes, sessionsRes] =
        await Promise.all([
          this.get({
            url: `${domain}PlannerConcepts/GetByPagination?db=${this.db}&campusID=${this.campusId}&page=1&pageSize=1000`,
            token,
          }),
          this.get({
            url: `${domain}PlannerMainTopics/GetByPagination?db=${this.db}&campusID=${this.campusId}&page=1&pageSize=1000`,
            token,
          }),
          this.get({
            url: `${domain}PlannerSubTopics/GetByPagination?db=${this.db}&campusID=${this.campusId}&page=1&pageSize=1000`,
            token,
          }),
          this.get({
            url: `${domain}myclasses/LoadClasses?db=${this.db}&cId=${this.campusId}&dID=0`,
            token,
          }),
          this.get({
            url: `${domain}Subjects?db=${this.db}&cId=${this.campusId}`,
            token,
          }),
          this.get({
            url: `${domain}Sessions?db=${this.db}&cID=${this.campusId}`,
            token,
          }),
        ]);

      this.concepts = this.normalizeList(conceptsRes).map((item) => ({
        ...item,
        concept: this.cleanConceptText(item.concept),
      }));
      this.topics = this.normalizeList(topicsRes).map((item) => ({
        id: item.id,
        mainTopic: item.topic || item.mainTopic,
      }));
      this.subtopics = this.normalizeList(subtopicsRes).map((item) => ({
        id: item.id,
        subTopic: item.topic || item.subTopic,
      }));
      this.classes = this.normalizeClasses(classesRes);
      this.subjects = Array.isArray(subjectsRes) ? subjectsRes : [];
      this.sessions = this.normalizeList(sessionsRes);
    },

    async loadSheetData() {
      const response = await this.get({
        url: `${this.$store.state.domain}PlannerSheet/LoadSelected/${this.campusId}/${this.sheetId}?db=${this.db}`,
        token: this.$store.state.userData.token,
      });

      if (!response) {
        throw new Error("Empty planner sheet response");
      }

      this.plannerSheet = response.plannerSheet || {};
      this.plannerDetails = response.plannerSheetDetails || [];

      if (this.plannerSheet.sectionID) {
        await this.loadSections(this.plannerSheet.classID);
      }
    },

    async loadSections(classID) {
      if (!classID) return;
      const res = await this.get({
        url: `${this.$store.state.domain}myclasses/LoadClasses?db=${this.db}&cId=${this.campusId}&dID=0`,
        token: this.$store.state.userData.token,
      });
      const classes = this.normalizeClasses(res);
      const match = classes.find((item) => item.cls && item.cls.id === classID);
      this.sections = match && Array.isArray(match.sections) ? match.sections : [];
    },

    buildGridRows() {
      const details = Array.isArray(this.plannerDetails)
        ? this.plannerDetails
        : [];
      const rowsMap = {};

      details.forEach((detail) => {
        const detailType = detail.type || "None";
        const key = `${detail.conceptID}-${detail.mainTopicID}-${detail.subTopicID}-${detailType}`;

        if (!rowsMap[key]) {
          const concept = this.concepts.find((c) => c.id === detail.conceptID);
          const topic = this.topics.find((t) => t.id === detail.mainTopicID);
          const subtopic = this.subtopics.find((s) => s.id === detail.subTopicID);

          rowsMap[key] = {
            key,
            concept: concept?.concept || "",
            topic: topic?.mainTopic || "",
            subtopic: subtopic?.subTopic || "",
            type: detailType,
            headers: {},
          };
        }

        rowsMap[key].headers[detail.headerID] = this.processContentForDisplay(
          detail.remarks || ""
        );
      });

      this.gridRows = Object.values(rowsMap).filter(
        (row) =>
          row.concept ||
          row.topic ||
          row.subtopic ||
          Object.values(row.headers).some((value) => String(value || "").trim())
      );
    },

    resolveMetaLabels() {
      const sheet = this.plannerSheet || {};

      if (sheet.classID) {
        const cls = this.classes.find((item) => item.cls?.id === sheet.classID);
        if (cls?.cls?.name) this.gradeName = cls.cls.name;
      }

      if (sheet.subjectID) {
        const subject = this.subjects.find((item) => item.id === sheet.subjectID);
        if (subject?.subject) this.subjectName = subject.subject;
      }

      if (sheet.sessionID) {
        const session = this.sessions.find((item) => item.id === sheet.sessionID);
        if (session?.session) {
          this.sessionName = session.session;
        }
      }

      if (sheet.sectionID === 0) {
        this.sectionName = "All";
      } else if (sheet.sectionID) {
        const section = this.sections.find((item) => item.id === sheet.sectionID);
        if (section?.section) this.sectionName = section.section;
      }

      this.teacherName =
        sheet.teacherName ||
        sheet.teacher?.name ||
        sheet.addedByName ||
        this.$store.state.userData?.name ||
        "—";
    },

    normalizeList(response) {
      if (Array.isArray(response)) return response;
      if (response && Array.isArray(response.data)) return response.data;
      return [];
    },

    normalizeClasses(response) {
      if (Array.isArray(response)) return response;
      if (response && Array.isArray(response.data)) return response.data;
      return [];
    },

    cleanConceptText(text) {
      if (!text) return "";
      return String(text)
        .replace(/^(VIII|IX|VII|VI|V|IV|III|II|I)\s*-\s*/i, "")
        .replace(/^(Class\s+\d+|Grade\s+\d+)\s*-\s*/i, "")
        .replace(/^(\d+)\s*-\s*/, "")
        .trim();
    },

    stripHtmlTags(text) {
      if (!text) return "";
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = text;
      return (tempDiv.textContent || tempDiv.innerText || "").trim();
    },

    processContentForDisplay(content) {
      if (!content) return "";
      const value = String(content).trim();
      if (!value.includes("<")) return value;
      return this.stripHtmlTags(value);
    },

    isClassTestOrQuiz(type) {
      const normalized = String(type || "").toLowerCase();
      return normalized === "class test" || normalized === "quiz";
    },

    print() {
      const previousTitle = document.title;
      document.title = " ";
      const restoreTitle = () => {
        document.title = this.originalPageTitle || previousTitle;
        window.removeEventListener("afterprint", restoreTitle);
      };
      window.addEventListener("afterprint", restoreTitle);
      window.print();
    },

    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.planner-sheet-aba {
  min-height: 100vh;
  background: #f3f4f6;
  padding: 1rem;
}

.control-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
}

.btn-modern {
  display: inline-flex;
  align-items: center;
  border: none;
  border-radius: 8px;
  padding: 0.55rem 0.95rem;
  font-weight: 600;
  cursor: pointer;
}

.btn-print {
  background: #7367f0;
  color: #fff;
}

.btn-close {
  background: #eef2ff;
  color: #4b5563;
}

.loading-state,
.error-state {
  text-align: center;
  padding: 3rem 1rem;
  background: #fff;
  border-radius: 12px;
}

.print-page {
  max-width: 1400px;
  margin: 0 auto;
}

.sheet-border {
  background: #fff;
  border: 2px solid #111827;
  padding: 1.25rem;
  color: #111827;
}

.sheet-header {
  text-align: center;
  margin-bottom: 1rem;
}

.sheet-title {
  font-size: 1.6rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: 0.02em;
  color: #000;
}

.meta-grid {
  border: 1.5px solid #111827;
  margin-bottom: 1rem;
}

.meta-row {
  display: grid;
  grid-template-columns: 150px 1fr 120px 1fr;
  border-bottom: 1px solid #111827;

  &:last-child {
    border-bottom: none;
  }
}

.meta-cell {
  padding: 0.45rem 0.6rem;
  border-right: 1px solid #111827;
  font-size: 0.92rem;
  line-height: 1.35;
  color: #111827;

  &:last-child {
    border-right: none;
  }
}

.meta-label {
  font-weight: 700;
  background: #f3f4f6;
  color: #000;
}

.meta-value {
  font-weight: 600;
  color: #111827;
}

.meta-value--wide {
  grid-column: span 3;
}

.grid-wrap {
  overflow-x: auto;
}

.lesson-grid {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.lesson-grid th,
.lesson-grid td {
  border: 1.5px solid #111827;
  padding: 0.45rem 0.5rem;
  vertical-align: top;
  font-size: 0.84rem;
  line-height: 1.35;
  word-break: break-word;
  color: #111827;
}

.lesson-grid th {
  background: #e5e7eb;
  font-weight: 700;
  text-align: center;
  color: #000;
}

.col-index {
  width: 42px;
}

.col-concept,
.col-topic,
.col-subtopic {
  width: 140px;
}

.col-type {
  width: 90px;
}

.period-cell {
  min-width: 120px;
}

.type-flag {
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  color: #92400e;
  margin-bottom: 0.2rem;
}

.empty-cell {
  text-align: center;
  padding: 1.5rem;
  color: #374151;
  font-weight: 600;
}

@page {
  size: A4 portrait;
  margin: 0;
}

@media print {
  .no-print {
    display: none !important;
  }

  html,
  body {
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
    color: #000 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .planner-sheet-aba {
    background: #fff !important;
    padding: 0 !important;
    min-height: auto !important;
  }

  .print-page {
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
  }

  .sheet-border {
    border: 2px solid #000 !important;
    box-shadow: none !important;
    padding: 1cm 0.8cm !important;
    margin: 0 !important;
  }

  .sheet-title {
    font-size: 1.35rem !important;
    color: #000 !important;
  }

  .meta-cell,
  .meta-label,
  .meta-value {
    color: #000 !important;
    font-size: 9pt !important;
  }

  .lesson-grid {
    width: 100% !important;
    font-size: 8pt !important;
  }

  .lesson-grid th,
  .lesson-grid td {
    border: 1px solid #000 !important;
    padding: 4px 5px !important;
    color: #000 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .lesson-grid th {
    background: #e5e7eb !important;
    color: #000 !important;
    font-weight: 800 !important;
  }

  .meta-grid,
  .meta-row,
  .meta-cell {
    border-color: #000 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .grid-wrap {
    overflow: visible !important;
  }
}
</style>

<style lang="scss">
@page {
  size: A4 portrait;
  margin: 0;
}

@media print {
  html,
  body {
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
  }
}
</style>
